"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useGoogleMaps } from "@/providers/GoogleMapsProvider";

export interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text?: string;
  };
  types?: string[];
}

export interface UseLocationAutocompleteOptions {
  debounceMs?: number;
  searchOptions?: {
    types?: string[];
    componentRestrictions?: google.maps.places.ComponentRestrictions;
    location?: google.maps.LatLng | google.maps.LatLngLiteral;
    radius?: number;
  };
  onPlaceSelect?: (place: PlacePrediction) => void;
  onSearch?: (value: string) => void;
}

export interface UseLocationAutocompleteReturn {
  inputValue: string;
  setInputValue: (value: string) => void;
  predictions: PlacePrediction[];
  showDropdown: boolean;
  selectedIndex: number;
  isLoaded: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleValueChange: (value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handlePlaceSelect: (place: PlacePrediction) => void;
  closeDropdown: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function useLocationAutocomplete(
  options: UseLocationAutocompleteOptions = {}
): UseLocationAutocompleteReturn {
  const {
    debounceMs = 700,
    searchOptions = {},
    onPlaceSelect,
    onSearch,
  } = options;

  const { isLoaded, placesService } = useGoogleMaps();

  const [inputValue, setInputValue] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const fetchPredictions = useCallback(
    (input: string) => {
      if (!placesService || !input.trim()) {
        setPredictions([]);
        return;
      }

      if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      const request: google.maps.places.AutocompletionRequest = {
        input,
        componentRestrictions: searchOptions.componentRestrictions,
        sessionToken: sessionTokenRef.current || undefined,
      };

      if (searchOptions.types) {
        request.types = searchOptions.types;
      }

      placesService.getPlacePredictions(request, (results, status) => {
        if (status === "OK" && results) {
          const formattedPredictions: PlacePrediction[] = results.map(
            (result) => ({
              description: result.description,
              place_id: result.place_id,
              structured_formatting: result.structured_formatting,
              types: result.types,
            })
          );
          setPredictions(formattedPredictions);
          setShowDropdown(true);
        } else {
          setPredictions([]);
        }
      });
    },
    [placesService, searchOptions]
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setInputValue(value);
      setSelectedIndex(-1);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (value.trim()) {
        debounceTimerRef.current = setTimeout(() => {
          fetchPredictions(value);
        }, debounceMs);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    },
    [fetchPredictions, debounceMs]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleValueChange(e.target.value);
    },
    [handleValueChange]
  );

  const handlePlaceSelect = useCallback(
    (place: PlacePrediction) => {
      setInputValue(place.description);
      setShowDropdown(false);
      setPredictions([]);

      sessionTokenRef.current = null;

      if (onPlaceSelect) {
        onPlaceSelect(place);
      } else if (onSearch) {
        onSearch(place.description);
      }
    },
    [onPlaceSelect, onSearch]
  );

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSelectedIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || predictions.length === 0) {
        if (e.key === "Enter" && onSearch) {
          e.preventDefault();
          onSearch(inputValue);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < predictions.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < predictions.length) {
            const selectedPrediction = predictions[selectedIndex];
            if (selectedPrediction) {
              handlePlaceSelect(selectedPrediction);
            }
          } else if (onSearch) {
            onSearch(inputValue);
            setShowDropdown(false);
          }
          break;
        case "Escape":
          closeDropdown();
          break;
      }
    },
    [
      showDropdown,
      predictions,
      selectedIndex,
      inputValue,
      onSearch,
      handlePlaceSelect,
      closeDropdown,
    ]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    inputValue,
    setInputValue,
    predictions,
    showDropdown,
    selectedIndex,
    isLoaded,
    handleInputChange,
    handleValueChange,
    handleKeyDown,
    handlePlaceSelect,
    closeDropdown,
    containerRef,
  };
}
